/**
 * WATCHDOG D'UPLOAD — module PUR (horloge injectée), testable sans XHR ni réseau.
 *
 * POURQUOI PAS `xhr.timeout`. Le transfert d'un fichier n'a pas de durée « normale » : 450 Mo
 * sur une ligne lente prennent légitimement des dizaines de minutes. Un timeout ABSOLU
 * (`xhr.timeout`) pénaliserait donc exactement les dépôts qu'on cherche à rendre fiables — il
 * tuerait un transfert en bonne santé au seul motif qu'il est gros. C'est aussi pourquoi le
 * garde-temps de `apiFetch` (60 s, cf. `lib/timeout.ts`) NE s'applique pas ici.
 *
 * Ce qu'il faut détecter n'est pas la LENTEUR mais l'ARRÊT : une socket ouverte qui ne
 * transporte plus rien (Wi-Fi coupé sans FIN, proxy qui a lâché en silence, machine en veille
 * réseau). Le signal fiable est donc l'ABSENCE DE PROGRESSION, pas le temps écoulé — et il est
 * indépendant de la taille du fichier.
 *
 * ⚠ État constaté avant ce module : `xhr.timeout` n'était JAMAIS assigné, donc valait `0`
 * (illimité par défaut), ce qui rendait le handler `xhr.ontimeout` STRUCTURELLEMENT MORT — du
 * code de reprise qui n'avait aucun moyen de s'exécuter.
 *
 * DEUX FENÊTRES, parce que ce sont deux pannes différentes :
 *  1. CORPS EN VOL — chaque `xhr.upload.onprogress` réarme le compte à rebours ; plus rien
 *     pendant {@link STALL_TIMEOUT_MS} ⇒ transfert bloqué.
 *  2. RÉPONSE ATTENDUE — les octets sont partis, le serveur travaille (hachage, contrôle
 *     d'intégrité, ré-encodage d'image sans perte). Aucune progression n'est ALORS attendue :
 *     appliquer la fenêtre courte y couperait un traitement serveur parfaitement sain. On
 *     bascule donc sur {@link RESPONSE_TIMEOUT_MS}, large mais FINI.
 *
 * Le déclenchement est classé RÉCUPÉRABLE par l'appelant : une reprise réussit typiquement,
 * et le préflight par hash évite de retransférer ce qui est déjà arrivé.
 */

/** Absence de progression tolérée pendant que le CORPS est en vol (ms). */
export const STALL_TIMEOUT_MS = 60_000;

/**
 * Attente tolérée pour la RÉPONSE, une fois tous les octets envoyés (ms). Large : le serveur
 * hache, vérifie l'intégrité et peut ré-encoder l'image (T8) avant de répondre.
 */
export const RESPONSE_TIMEOUT_MS = 300_000;

/** Horloge injectable (les tests fournissent des doublures déterministes). */
export interface WatchdogTimers {
  setTimeout: (fn: () => void, ms: number) => number;
  clearTimeout: (id: number) => void;
}

// `window.*` (et non `globalThis.*`) : en DOM ces surcharges renvoient un `number`, là où les
// typages Node renverraient un `Timeout` — même convention que le reste du module d'upload.
const defaultTimers: WatchdogTimers = {
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (id) => window.clearTimeout(id),
};

/** Chien de garde d'UN transfert : à réarmer sur progression, à arrêter à l'issue. */
export interface Watchdog {
  /** Progression observée : réarme la fenêtre COURTE. Sans effet une fois arrêté. */
  ping: () => void;
  /** Corps entièrement envoyé : bascule sur la fenêtre LARGE d'attente de réponse. */
  awaitResponse: () => void;
  /** Arrêt DÉFINITIF (succès, erreur, abandon) — idempotent, ne déclenche plus rien. */
  stop: () => void;
}

/**
 * Démarre un watchdog. `onStall` est appelé AU PLUS UNE FOIS, et jamais après `stop()` — sans
 * quoi une réponse arrivée juste avant l'échéance produirait à la fois un succès et un échec.
 */
export function startWatchdog(
  onStall: () => void,
  timers: WatchdogTimers = defaultTimers,
): Watchdog {
  let timer: number | null = null;
  let stopped = false;

  const arm = (ms: number): void => {
    if (stopped) return;
    if (timer !== null) timers.clearTimeout(timer);
    timer = timers.setTimeout(() => {
      timer = null;
      if (stopped) return;
      stopped = true; // au plus un déclenchement
      onStall();
    }, ms);
  };

  const stop = (): void => {
    stopped = true;
    if (timer !== null) {
      timers.clearTimeout(timer);
      timer = null;
    }
  };

  arm(STALL_TIMEOUT_MS);

  return {
    ping: () => arm(STALL_TIMEOUT_MS),
    awaitResponse: () => arm(RESPONSE_TIMEOUT_MS),
    stop,
  };
}
